import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Camera, CheckCircle2, AlertCircle, Upload, X,
  MapPin, Clock, Image as ImageIcon, ShieldCheck, AlertTriangle, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChecklistPage() {
  const navigate = useNavigate();
  const { checklistId } = useParams();
  const fileInputRef = useRef(null);

  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [validationResult, setValidationResult] = useState(null);

  // Upload modal state
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [uploadNote, setUploadNote] = useState('');
  const [capturedImage, setCapturedImage] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);

  useEffect(() => {
    fetchChecklist();
    requestGPS();
  }, [checklistId]);

  const fetchChecklist = async () => {
    try {
      const response = await axios.get(`${API}/checklists/${checklistId}`, { withCredentials: true });
      setChecklist(response.data);
    } catch (err) {
      setError('Failed to load checklist');
    } finally {
      setLoading(false);
    }
  };

  const requestGPS = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (err) => {
          console.log('GPS not available:', err);
        }
      );
    }
  };

  const handleItemToggle = async (item, isRequired) => {
    const newStatus = item.status === 'verified' ? 'missing' : 'verified';
    // Allow toggling without photo - user can add photo separately
    await updateItemStatus(item.id, newStatus, item.photo_id, item.count, item.note);
  };

  const handleOpenUpload = (item, isRequired) => {
    setSelectedItem({ ...item, isRequired });
    setUploadNote('');
    setCapturedImage(null);
    setUploadModalOpen(true);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCapturedImage(file);
      setValidationResult(null);
    }
  };

  const handleValidatePhoto = async () => {
    if (!capturedImage || !selectedItem) return;
    
    setValidating(true);
    setValidationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', capturedImage);
      
      const params = new URLSearchParams({
        item_id: selectedItem.id,
        item_name: selectedItem.name
      });
      
      const response = await axios.post(
        `${API}/photos/validate?${params.toString()}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      setValidationResult(response.data);
      
      if (response.data.is_valid) {
        toast.success('Photo validated successfully!');
      } else {
        toast.warning(response.data.message || 'Photo validation failed');
      }
    } catch (err) {
      console.error('Validation failed:', err);
      setValidationResult({
        is_valid: true,
        message: 'Validation skipped - proceed with upload'
      });
    } finally {
      setValidating(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (!capturedImage || !selectedItem) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', capturedImage);

      const params = new URLSearchParams({
        item_id: selectedItem.id,
        checklist_id: checklistId,
      });
      if (gpsLocation) {
        params.append('latitude', gpsLocation.latitude.toString());
        params.append('longitude', gpsLocation.longitude.toString());
      }
      if (uploadNote) {
        params.append('note', uploadNote);
      }

      const uploadResponse = await axios.post(
        `${API}/photos/upload?${params.toString()}`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Show AI validation result if available
      if (uploadResponse.data.ai_validation) {
        const aiResult = uploadResponse.data.ai_validation;
        if (aiResult.is_valid && aiResult.confidence >= 70) {
          toast.success(`AI verified: ${aiResult.equipment_detected}`);
        } else if (aiResult.confidence < 50) {
          toast.warning('Photo may not show valid equipment');
        }
      }

      // Update item status with photo
      await updateItemStatus(selectedItem.id, 'verified', uploadResponse.data.photo_id, selectedItem.count, uploadNote);

      setUploadModalOpen(false);
      setCapturedImage(null);
      setUploadNote('');
      setValidationResult(null);
    } catch (err) {
      setError('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const updateItemStatus = async (itemId, status, photoId, count, note) => {
    try {
      const response = await axios.put(`${API}/checklists/${checklistId}`, {
        items: [{
          item_id: itemId,
          status,
          photo_id: photoId,
          count,
          note
        }]
      }, { withCredentials: true });
      setChecklist(response.data);
    } catch (err) {
      setError('Failed to update item');
    }
  };

  const handleUpdateCount = async (item, count) => {
    await updateItemStatus(item.id, item.status, item.photo_id, count, item.note);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#2B3A67] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!checklist) {
    return (
      <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-6">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-[#D32F2F] mx-auto mb-4" />
          <p className="text-[#D32F2F] font-bold">{error || 'Checklist not found'}</p>
          <Button onClick={() => navigate('/dashboard')} className="mt-4">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const totalRequired = checklist.required_items.length;
  const verifiedRequired = checklist.required_items.filter(i => i.status === 'verified').length;
  const progress = totalRequired > 0 ? (verifiedRequired / totalRequired) * 100 : 0;

  return (
    <div className="min-h-screen bg-[#F0F4F8] pb-32 page-enter">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-xl border-b-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              data-testid="checklist-back-btn"
              onClick={() => navigate('/dashboard')}
              className="text-[#2B3A67]"
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div className="text-center">
              <h1 className="text-lg font-bold text-[#2B3A67] font-['Outfit']">Safety Checklist</h1>
              <p className="text-sm text-[#4A5568]">{checklist.vessel_name} • Class {checklist.vessel_class}</p>
            </div>
            <div className="w-10"></div>
          </div>
          
          {/* Progress */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="font-medium text-[#4A5568]">Completion</span>
              <span className="font-bold text-[#2B3A67]">{verifiedRequired}/{totalRequired} items</span>
            </div>
            <Progress value={progress} className="h-4 bg-[#E2E8F0]" />
          </div>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-6">
        {/* Trip Info */}
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-[#E2E8F0]">
            <MapPin className="w-4 h-4 text-[#4A5568]" />
            <span className="font-medium text-[#2B3A67] capitalize">{checklist.location}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-full border border-[#E2E8F0]">
            <Clock className="w-4 h-4 text-[#4A5568]" />
            <span className="font-medium text-[#2B3A67] capitalize">{checklist.time_of_day}</span>
          </div>
          {gpsLocation && (
            <div className="flex items-center gap-2 px-3 py-2 bg-[#E8F6ED] rounded-full border border-[#3CB371]/30">
              <MapPin className="w-4 h-4 text-[#3CB371]" />
              <span className="font-medium text-[#2A8A53]">GPS Active</span>
            </div>
          )}
        </div>

        {/* Required Items */}
        <div>
          <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit'] mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-[#D32F2F]" />
            Required Items
          </h2>
          <div className="space-y-3">
            {checklist.required_items.map((item) => (
              <ChecklistItem
                key={item.id}
                item={item}
                isRequired={true}
                onToggle={() => handleItemToggle(item, true)}
                onUpload={() => handleOpenUpload(item, true)}
                onCountChange={(count) => handleUpdateCount(item, count)}
                checklistId={checklistId}
              />
            ))}
          </div>
        </div>

        {/* Recommended Items */}
        {checklist.recommended_items.length > 0 && (
          <div>
            <h2 className="text-lg font-bold text-[#2B3A67] font-['Outfit'] mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#3CB371]" />
              Recommended Items
            </h2>
            <div className="space-y-3">
              {checklist.recommended_items.map((item) => (
                <ChecklistItem
                  key={item.id}
                  item={item}
                  isRequired={false}
                  onToggle={() => handleItemToggle(item, false)}
                  onUpload={() => handleOpenUpload(item, false)}
                  onCountChange={(count) => handleUpdateCount(item, count)}
                  checklistId={checklistId}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#E2E8F0] px-6 py-4">
        <div className="max-w-lg mx-auto">
          <Button
            data-testid="view-results-btn"
            onClick={() => navigate(`/result/${checklistId}`)}
            disabled={verifiedRequired === 0}
            className={`w-full h-14 font-bold text-lg rounded-xl transition-all active:scale-[0.98] ${
              progress === 100
                ? 'bg-[#3CB371] hover:bg-[#2A8A53] text-white'
                : 'bg-[#2B3A67] hover:bg-[#1A264A] text-white'
            }`}
          >
            {progress === 100 ? (
              <>
                <ShieldCheck className="w-5 h-5 mr-2" />
                View Compliance Result
              </>
            ) : (
              `View Results (${Math.round(progress)}% Complete)`
            )}
          </Button>
        </div>
      </div>

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={(open) => {
        if (!open) {
          setUploadModalOpen(false);
          setCapturedImage(null);
          setUploadNote('');
        }
      }}>
        <DialogContent className="max-w-md mx-auto bg-white" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#2B3A67] font-['Outfit']">
              Upload Photo
            </DialogTitle>
            <DialogDescription className="text-[#4A5568]">
              {selectedItem?.name} - Take or upload a photo for verification
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Photo Preview or Upload Area */}
            {capturedImage ? (
              <div className="relative">
                <img
                  src={URL.createObjectURL(capturedImage)}
                  alt="Preview"
                  className="w-full h-48 object-cover rounded-xl"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                  onClick={() => setCapturedImage(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed border-[#E2E8F0] rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#2B3A67] transition-colors bg-[#F0F4F8]"
              >
                <Camera className="w-12 h-12 text-[#4A5568] mb-2" />
                <p className="font-medium text-[#2B3A67]">Tap to take photo</p>
                <p className="text-sm text-[#4A5568]">or upload from gallery</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              className="hidden"
            />

            {/* GPS Info */}
            {gpsLocation && (
              <div className="flex items-center gap-2 p-3 bg-[#E8F6ED] rounded-xl">
                <MapPin className="w-5 h-5 text-[#3CB371]" />
                <span className="text-sm font-medium text-[#2A8A53]">
                  GPS: {gpsLocation.latitude.toFixed(4)}, {gpsLocation.longitude.toFixed(4)}
                </span>
              </div>
            )}

            {/* AI Validation Result */}
            {validationResult && (
              <div className={`p-3 rounded-xl ${
                validationResult.is_valid 
                  ? 'bg-[#E8F6ED] border border-[#3CB371]/30' 
                  : 'bg-[#FDF3E1] border border-[#E5A000]/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {validationResult.is_valid ? (
                    <CheckCircle2 className="w-5 h-5 text-[#3CB371]" />
                  ) : (
                    <AlertTriangle className="w-5 h-5 text-[#E5A000]" />
                  )}
                  <span className={`font-bold text-sm ${
                    validationResult.is_valid ? 'text-[#2A8A53]' : 'text-[#E5A000]'
                  }`}>
                    AI Validation: {validationResult.is_valid ? 'Passed' : 'Warning'}
                  </span>
                </div>
                <p className="text-sm text-[#4A5568]">{validationResult.message}</p>
                {validationResult.validation_result?.equipment_detected && (
                  <p className="text-xs text-[#4A5568] mt-1">
                    Detected: {validationResult.validation_result.equipment_detected}
                  </p>
                )}
              </div>
            )}

            {/* AI Validate Button */}
            {capturedImage && !validationResult && (
              <Button
                variant="outline"
                onClick={handleValidatePhoto}
                disabled={validating}
                className="w-full h-10 border-2 border-[#2B3A67] text-[#2B3A67] rounded-xl font-medium"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {validating ? 'Validating with AI...' : 'Validate Photo with AI'}
              </Button>
            )}

            {/* Note */}
            <Textarea
              data-testid="upload-note-input"
              placeholder="Add a note (optional)"
              value={uploadNote}
              onChange={(e) => setUploadNote(e.target.value)}
              className="border-2 border-[#E2E8F0] rounded-xl resize-none"
              rows={2}
            />

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setUploadModalOpen(false);
                  setCapturedImage(null);
                  setUploadNote('');
                  setValidationResult(null);
                }}
                className="flex-1 h-12 border-2 border-[#E2E8F0] rounded-xl font-bold"
              >
                Cancel
              </Button>
              <Button
                data-testid="confirm-upload-btn"
                onClick={handleUploadPhoto}
                disabled={!capturedImage || uploading}
                className="flex-1 h-12 bg-[#3CB371] hover:bg-[#2A8A53] text-white rounded-xl font-bold"
              >
                {uploading ? 'Uploading...' : 'Upload & Verify'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Checklist Item Component
function ChecklistItem({ item, isRequired, onToggle, onUpload, onCountChange, checklistId }) {
  const getStatusColor = () => {
    if (item.status === 'verified') return 'border-[#3CB371] bg-[#E8F6ED]';
    if (item.status === 'skipped') return 'border-[#E5A000] bg-[#FDF3E1]';
    return 'border-[#E2E8F0] bg-white';
  };

  const getStatusBadge = () => {
    if (item.status === 'verified') {
      return (
        <span className="inline-flex items-center gap-1 bg-[#E8F6ED] text-[#2A8A53] px-2 py-1 rounded-full text-xs font-bold">
          <CheckCircle2 className="w-3 h-3" />
          Verified
        </span>
      );
    }
    if (item.status === 'skipped') {
      return (
        <span className="inline-flex items-center bg-[#FDF3E1] text-[#E5A000] px-2 py-1 rounded-full text-xs font-bold">
          Skipped
        </span>
      );
    }
    return (
      <span className="inline-flex items-center bg-[#FAEAEA] text-[#D32F2F] px-2 py-1 rounded-full text-xs font-bold">
        Missing
      </span>
    );
  };

  return (
    <Card
      data-testid={`checklist-item-${item.id}`}
      className={`border-2 transition-all ${getStatusColor()}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <Checkbox
            data-testid={`item-checkbox-${item.id}`}
            checked={item.status === 'verified'}
            onCheckedChange={onToggle}
            className="w-7 h-7 mt-1 rounded-lg border-2"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <h3 className="font-bold text-[#2B3A67]">{item.name}</h3>
              {getStatusBadge()}
            </div>
            <p className="text-sm text-[#4A5568] mt-1">{item.description}</p>

            {/* Count input for life jackets */}
            {item.requires_count && (
              <div className="flex items-center gap-2 mt-3">
                <span className="text-sm font-medium text-[#4A5568]">Count:</span>
                <Input
                  type="number"
                  min="0"
                  data-testid={`item-count-${item.id}`}
                  value={item.count || ''}
                  onChange={(e) => onCountChange(parseInt(e.target.value) || 0)}
                  className="w-20 h-10 text-center border-2 border-[#E2E8F0] rounded-lg"
                />
              </div>
            )}

            {/* Photo upload button */}
            <Button
              variant="outline"
              size="sm"
              data-testid={`item-upload-${item.id}`}
              onClick={onUpload}
              className={`mt-3 h-10 rounded-lg font-medium ${
                item.photo_id
                  ? 'border-[#3CB371] text-[#3CB371] bg-[#E8F6ED]'
                  : 'border-[#E2E8F0] text-[#4A5568]'
              }`}
            >
              {item.photo_id ? (
                <>
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Photo Added
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4 mr-2" />
                  Add Photo
                </>
              )}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
